import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Star, Plus, Send, Trash2, Loader2, BarChart3, Users, Link2, Check, Download } from "lucide-react";
import { encuestasApi } from "../../lib/api";
import { PageHeader } from "../../components/PageHeader";
import { CrearEncuestaModal } from "./CrearEncuestaModal";
import { EncuestaDetalleModal } from "./EncuestaDetalleModal";

export default function EncuestasPage() {
  const qc = useQueryClient();
  const [creando, setCreando] = useState(false);
  const [detalleId, setDetalleId] = useState<number | null>(null);
  const [enviando, setEnviando] = useState<number | null>(null);
  const [copiado, setCopiado] = useState<number | null>(null);

  const lista = useQuery({
    queryKey: ["encuestas"],
    queryFn: () => encuestasApi.listar(),
  });

  const eliminarMut = useMutation({
    mutationFn: (id: number) => encuestasApi.eliminar(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["encuestas"] }),
  });

  function copiarLinkQr(e: { id: number; tokenQr?: string | null }) {
    if (!e.tokenQr) return;
    const url = `${window.location.origin}/encuesta-qr/${e.tokenQr}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiado(e.id);
      setTimeout(() => setCopiado(null), 2000);
    });
  }

  function descargarQr(e: { tokenQr?: string | null; titulo: string }) {
    if (!e.tokenQr) return;
    const url = `${window.location.origin}/encuesta-qr/${e.tokenQr}`;
    const imgUrl = `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(url)}&format=png&margin=8&download=1`;
    const a = document.createElement("a");
    a.href = imgUrl;
    a.download = `encuesta-qr-${e.titulo.replace(/\s+/g, "-").toLowerCase()}.png`;
    a.click();
  }

  async function enviarMasivo(id: number) {
    setEnviando(id);
    try {
      const r = await encuestasApi.enviarMasivo(id);
      alert(`Enviadas: ${r.enviados} · Omitidas: ${r.omitidos} (de ${r.totalClientes} clientes con teléfono)`);
      qc.invalidateQueries({ queryKey: ["encuestas"] });
    } catch (e: any) {
      alert(e.message || "Error enviando la encuesta");
    } finally {
      setEnviando(null);
    }
  }

  return (
    <div className="min-h-screen bg-ink-50">
      <PageHeader
        crumbs={[{ label: "Tienda", to: "/tienda" }, { label: "Encuestas" }]}
        icon={Star}
        actions={[{ label: "Nueva encuesta", icon: Plus, variant: "primary", onClick: () => setCreando(true) }]}
      />

      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6">
        <div className="card">
          {lista.isLoading ? (
            <div className="p-10 text-center text-ink-400">
              <Loader2 className="size-5 animate-spin mx-auto" />
            </div>
          ) : !lista.data || lista.data.length === 0 ? (
            <div className="p-12 text-center text-ink-400">
              <Star className="size-12 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Todavía no creaste ninguna encuesta</p>
              <p className="text-xs mt-1">Creá una con "Nueva encuesta" arriba</p>
            </div>
          ) : (
            <div className="divide-y divide-ink-100">
              {lista.data.map((e) => (
                <div key={e.id} className="px-5 py-4 flex items-center gap-4">
                  <div className="size-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                    <Star className="size-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <button onClick={() => setDetalleId(e.id)} className="font-medium text-ink-800 hover:text-brand-700 hover:underline text-left inline-flex items-center gap-1.5">
                      {e.titulo}
                      {e.modo === "qr" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">QR</span>
                      )}
                    </button>
                    {e.descripcion && (
                      <p className="text-xs text-ink-500 truncate">{e.descripcion}</p>
                    )}
                    {e.sucursalNombre && (
                      <p className="text-[11px] text-brand-700 font-medium mt-0.5">{e.sucursalNombre}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-ink-400">
                      <span>{e.totalPreguntas} pregunta{e.totalPreguntas !== 1 ? "s" : ""}</span>
                      <span className="inline-flex items-center gap-1">
                        <Users className="size-3" /> {e.modo === "qr" ? "Enlace abierto" : `${e.totalEnviadas} enviadas`}
                      </span>
                      <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                        {e.totalRespondidas} respondidas
                      </span>
                    </div>
                  </div>
                  <button onClick={() => setDetalleId(e.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                    <BarChart3 className="size-3.5" /> Resultados
                  </button>
                  {e.modo === "qr" ? (
                    <>
                      <button onClick={() => descargarQr(e)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-ink-100 text-ink-700 hover:bg-ink-200">
                        <Download className="size-3.5" /> Descargar QR
                      </button>
                      <button onClick={() => copiarLinkQr(e)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-600 text-white hover:bg-brand-700">
                        {copiado === e.id ? <Check className="size-3.5" /> : <Link2 className="size-3.5" />}
                        {copiado === e.id ? "Copiado" : "Copiar enlace"}
                      </button>
                    </>
                  ) : (
                    <button onClick={() => enviarMasivo(e.id)} disabled={enviando === e.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50">
                      {enviando === e.id ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
                      Enviar masivo
                    </button>
                  )}
                  <button onClick={() => { if (confirm(`¿Eliminar "${e.titulo}"?`)) eliminarMut.mutate(e.id); }}
                    className="p-1.5 rounded hover:bg-red-100 text-red-600 shrink-0">
                    <Trash2 className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {creando && (
        <CrearEncuestaModal
          onClose={() => setCreando(false)}
          onCreated={() => { setCreando(false); qc.invalidateQueries({ queryKey: ["encuestas"] }); }}
        />
      )}
      {detalleId != null && (
        <EncuestaDetalleModal id={detalleId} onClose={() => setDetalleId(null)} />
      )}
    </div>
  );
}
